import React, { useReducer, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { syncDataToCloud, loadDataFromCloud } from '../services/cloudService';
import AppContext from './AppContext';

const appReducer = (state, action) => {
  let newState;
  
  switch (action.type) {
    case 'SET_USER':
      if (!action.payload) {
        // Clear local storage when user signs out
        localStorage.removeItem('shoppingList');
        localStorage.removeItem('recipeHistory');
        localStorage.removeItem('customLists');
        localStorage.removeItem('shoppingMode');
        localStorage.removeItem('themeColor');
        // Reset state to initial values
        return {
          ...state,
          user: null,
          isLoading: false,
          shoppingList: [],
          recipeHistory: [],
          customLists: [],
          shoppingMode: false,
          themeColor: '#1890ff'
        };
      }
      return { ...state, user: action.payload, isLoading: false };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'LOAD_CLOUD_DATA':
      return { ...state, ...action.payload, isLoading: false };

    case 'ADD_ITEM':
      newState = {
        ...state,
        shoppingList: [...state.shoppingList, {
          ...action.payload,
          purchased: false
        }]
      };
      localStorage.setItem('shoppingList', JSON.stringify(newState.shoppingList));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;
    
    case 'REMOVE_ITEM':
      newState = {
        ...state,
        shoppingList: state.shoppingList.filter(item => item.id !== action.payload)
      };
      localStorage.setItem('shoppingList', JSON.stringify(newState.shoppingList));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;
    
    case 'TOGGLE_PURCHASED':
      if (action.listType === 'custom') {
        const updatedCustomLists = state.customLists.map(list => ({
          ...list,
          items: list.items.map(item => 
            item.id === action.payload 
              ? { ...item, purchased: !item.purchased }
              : item
          )
        }));
        localStorage.setItem('customLists', JSON.stringify(updatedCustomLists));
        newState = { ...state, customLists: updatedCustomLists };
      } else {
        const toggledList = state.shoppingList.map(item => 
          item.id === action.payload 
            ? { ...item, purchased: !item.purchased }
            : item
        );
        localStorage.setItem('shoppingList', JSON.stringify(toggledList));
        newState = { ...state, shoppingList: toggledList };
      }
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'CLEAR_LIST':
      newState = { ...state, shoppingList: [] };
      localStorage.setItem('shoppingList', JSON.stringify([]));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;
    
    case 'TOGGLE_THEME':
      newState = { ...state, theme: state.theme === 'light' ? 'dark' : 'light' };
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;
    
    case 'TOGGLE_SHOPPING_MODE':
      newState = { ...state, shoppingMode: !state.shoppingMode };
      localStorage.setItem('shoppingMode', JSON.stringify(newState.shoppingMode));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;
    
    case 'SET_THEME_COLOR':
      newState = { ...state, themeColor: action.payload };
      localStorage.setItem('themeColor', action.payload);
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'SHOW_SIGN_IN_MODAL':
      return { ...state, showSignInModal: true };
    
    case 'REMOVE_DUPLICATES':
      const seen = new Set();
      const uniqueList = state.shoppingList.filter(item => {
        const key = `${item.name}-${item.measure}`;
        const isDuplicate = seen.has(key);
        seen.add(key);
        return !isDuplicate;
      });
      localStorage.setItem('shoppingList', JSON.stringify(uniqueList));
      return { ...state, shoppingList: uniqueList };
    
    case 'ADD_TO_HISTORY':
      const existingHistory = state.recipeHistory;
      const filteredHistory = existingHistory.filter(item => item.idMeal !== action.payload.idMeal);
      const updatedHistory = [action.payload, ...filteredHistory].slice(0, 20);
      localStorage.setItem('recipeHistory', JSON.stringify(updatedHistory));
      return { ...state, recipeHistory: updatedHistory };
    
    case 'CLEAR_HISTORY':
      localStorage.setItem('recipeHistory', JSON.stringify([]));
      return { ...state, recipeHistory: [] };

    case 'CLEAR_ALL_LISTS':
      newState = { 
        ...state, 
        shoppingList: [],
        customLists: []
      };
      localStorage.setItem('shoppingList', JSON.stringify([]));
      localStorage.setItem('customLists', JSON.stringify([]));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'ADD_CUSTOM_LIST':
      const newCustomLists = [...state.customLists || [], {
        id: Date.now().toString(),
        ...action.payload,
        items: action.payload.items.map(item => ({
          ...item,
          id: `${Date.now()}-${Math.random()}`,
          purchased: false
        }))
      }];
      newState = { ...state, customLists: newCustomLists };
      localStorage.setItem('customLists', JSON.stringify(newCustomLists));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'REMOVE_CUSTOM_LIST':
      const filteredCustomLists = state.customLists.filter(list => list.id !== action.payload);
      newState = { ...state, customLists: filteredCustomLists };
      localStorage.setItem('customLists', JSON.stringify(filteredCustomLists));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'EDIT_CUSTOM_LIST':
      const editedLists = state.customLists.map(list => 
        list.id === action.payload.id ? {
          ...list,
          name: action.payload.name,
          items: action.payload.items.map(item => ({
            ...item,
            id: item.id || `${Date.now()}-${Math.random()}`,
            purchased: item.purchased || false
          }))
        } : list
      );
      newState = { ...state, customLists: editedLists };
      localStorage.setItem('customLists', JSON.stringify(editedLists));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    case 'REMOVE_CUSTOM_LIST_ITEM':
      const updatedLists = state.customLists.map(list => {
        if (list.id === action.payload.listId) {
          return {
            ...list,
            items: list.items.filter(item => item.id !== action.payload.itemId)
          };
        }
        return list;
      });
      newState = { ...state, customLists: updatedLists };
      localStorage.setItem('customLists', JSON.stringify(updatedLists));
      if (state.user) {
        syncDataToCloud(state.user.uid, newState);
      }
      return newState;

    default:
      return state;
  }
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, AppContext._currentValue);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        dispatch({ type: 'SET_USER', payload: user });
        try {
          const cloudData = await loadDataFromCloud(user.uid);
          if (cloudData) {
            dispatch({ type: 'LOAD_CLOUD_DATA', payload: cloudData });
          } else {
            // First time user, sync current state to cloud
            syncDataToCloud(user.uid, state);
          }
        } catch (error) {
          console.error('Error loading cloud data:', error);
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch({ type: 'SET_USER', payload: null });
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider; 